import RAPIER from '@dimforge/rapier3d-compat'

import type { ColliderMode } from './colliderMode'
import {
  applyColliderMode,
  colliderModeFor,
  SENSOR_ACTIVE_COLLISION_TYPES,
} from './colliderMode'
import type { MeshColliderSource } from './colliderShapes'
import {
  colliderDescForShape,
  loadMeshColliderSource,
  MESH_COLLIDER_MIN_EXTENT,
  trimeshColliderDesc,
} from './colliderShapes'
import { physicalPropertiesFor } from './materialPhysicalProperties'
import {
  DYNAMIC_LINEAR_DAMPING,
  FALLEN_PARTS_DESTROY_HEIGHT,
  OBJECT_FLOAT_BUOYANCY_SPEED,
  OBJECT_FLOAT_CENTER_DEPTH_PER_HEIGHT,
  OBJECT_WATER_LINEAR_DAMPING,
} from './physicsConstants'
import { composeYaw, quatEquals, vec3Equals } from './poseMath'
import type {
  ObjectRuntimeState,
  PreviewWorld,
  SceneDescriptor,
  SceneObjectDescriptor,
  ShapeKind,
  Vec3,
} from '../types'
import { AVATAR_ID } from '../types'

const ZERO_VEC3 = { x: 0, y: 0, z: 0 }

const canCollideAtBirth = (object: SceneObjectDescriptor): boolean =>
  object.canCollide ?? (object.transparency ?? 0) < 1

// Anchored 化のたびに速度を恒久的にゼロ化する意図の rbxl Script を持つ、
// 調査済みのミッション・オブジェクト・Script 名の組み合わせ
// （"missionId/objectId/scriptName"）。object.id まで絞り込むのは、同一
// ミッション内に同名 Script を持つ無関係な別オブジェクトが存在しても
// 誤って対象化しないため（fail-closed）。
// 「Changed:Connect + "Anchored" + ゼロ代入」を汎用の正規表現でパターン
// マッチする案は、無関係なコードが近接するだけで誤検出した
// （例: 別オブジェクト向けの別ハンドラや、コメントアウトされた同型コード）。
// 全 24 ミッションの scenes JSON を調査した結果、該当パターンは以下の 2 件
// のみだったため、既知の組み合わせだけを明示的に許可する。
const PERMANENT_ANCHOR_ZERO_VELOCITY_SCRIPTS: ReadonlySet<string> = new Set([
  'RJS01/obj_15/Ball2Script',
  'RJS09/obj_46/BlockScript',
])

/**
 * オブジェクトの rbxl Script に、Anchored 化のたびに速度を恒久的にゼロ化する
 * 意図のパターンがあるか。true の場合、objectBodyRegistry は再アンカー時に
 * 速度を退避せず、次のアンカー解除で必ず静止状態から再開させる（Script の
 * 意図に合わせる）。
 */
const hasPermanentAnchorZeroVelocityScript = (
  missionId: string,
  object: SceneObjectDescriptor,
): boolean =>
  (object.scripts ?? []).some(
    (script) =>
      script.className === 'Script' &&
      script.disabled !== true &&
      PERMANENT_ANCHOR_ZERO_VELOCITY_SCRIPTS.has(
        `${missionId}/${object.id}/${script.name}`,
      ),
  )

/**
 * シーンオブジェクトの rapier body/collider を管理するレジストリ。
 *
 * createAvatarController から「オブジェクト body の生成・World↔rapier 同期・
 * リセット」のサブシステムを切り出したもの。body・collider・モードキャッシュ等の
 * Map/Set はファクトリ内部に隠蔽し、他サブシステム（touch 検知・carry・vehicle 等）
 * には lookup（objectIdOf / modeOf / has）と操作（reregister / sync /
 * writeBackDynamicBodies / reset / dispose）だけを公開する。
 */
export interface ObjectBodyRegistry {
  /** collider handle → objectId の逆引き（接触検出・接地相手の特定で使用）。 */
  objectIdOf(handle: number): string | undefined
  /** collider に適用済みの直近モード（sensor 重なり検知の判定で使用）。 */
  modeOf(id: string): ColliderMode | undefined
  /** id のオブジェクト body が存在するか（車両部品・座席の実在チェックで使用）。 */
  has(id: string): boolean
  /** コライダーを rapier の broad phase（クエリ構造）へ再登録させる。 */
  reregister(id: string): void
  /**
   * PreviewWorld の state を見て rapier 側のオブジェクト body/collider を同期する。
   * FALLEN_PARTS_DESTROY_HEIGHT より下のオブジェクトも除去する。
   */
  sync(): void
  /** 動的剛体（アンカー解除済み）の確定姿勢を World へ書き戻し、描画へ反映する。 */
  writeBackDynamicBodies(): void
  /** 全オブジェクト body を descriptor の初期状態へ戻す。 */
  reset(): void
  /** 内部の Map/Set を空にする（コントローラの dispose 用。body の解放は World 側で一括）。 */
  dispose(): void
}

/**
 * オブジェクト body レジストリを生成し、descriptor.objects の body を構築する。
 * RAPIER.init() 完了後に呼ぶこと。
 *
 * @param physicsWorld rapier の物理 World（body/collider の生成先）。
 * @param world 見た目・状態の真実源。state の参照元かつ動的姿勢の書き戻し先。
 * @param descriptor シーン記述。objects を読む（生成・reset の基準）。
 * @param isDisposed コントローラが破棄済みかのアクセサ（非同期メッシュ読込の中断判定）。
 * @param onAnchoredProgramMove sync が Fixed body のプログラム移動（離散テレポート）を
 *   適用した瞬間に呼ぶ。コントローラ側がアバターの押し出し要求の蓄積に使う
 *   （collider handle は KCC の障害物除外用）。
 * @param onProgramResize sync がサイズ変更コライダーを作り直した瞬間に呼ぶ。
 *   コントローラ側が、拡大で巻き込まれたアバターの押し出し要求を蓄積する。
 */
export const createObjectBodyRegistry = (
  physicsWorld: RAPIER.World,
  world: PreviewWorld,
  descriptor: SceneDescriptor,
  isDisposed: () => boolean,
  onAnchoredProgramMove: (
    id: string,
    state: ObjectRuntimeState,
    from: { x: number; y: number; z: number },
    to: { x: number; y: number; z: number },
    colliderHandle: number | undefined,
  ) => void,
  onProgramResize: (
    id: string,
    state: ObjectRuntimeState,
    previousSize: { x: number; y: number; z: number },
    nextSize: { x: number; y: number; z: number },
    colliderHandle: number | undefined,
  ) => void,
): ObjectBodyRegistry => {
  const descriptorObjectsById: ReadonlyMap<string, SceneObjectDescriptor> =
    new Map(descriptor.objects.map((object) => [object.id, object]))
  // collider handle → objectId の逆引き（接触検出で使用）。
  const colliderToObjectId = new Map<number, string>()
  // objectId → body（destroy 時の除去で使用。Fixed/Dynamic 両方を保持）。
  const objectBodies = new Map<string, RAPIER.RigidBody>()
  // objectId → collider（canCollide の毎ティック反映・除去時のハンドル逆引きで使用）。
  const objectColliders = new Map<string, RAPIER.Collider>()
  // アンカー解除で動的剛体（重力落下・転がり）になっているオブジェクト。
  // step 後に rapier の姿勢を World へ書き戻して描画へ反映する。
  const dynamicObjectIds = new Set<string>()
  // objectId → 生成時 Anchored だったか（World と同じ judge: isStatic !== false）。
  // Anchored 解除（movement_unsetAnchored）で Dynamic 化＝落下させてよいかの判定に使う。
  // isStatic 省略オブジェクトも Fixed body だが Anchored 扱いなので、両者を区別する。
  const objectWasAnchoredAtBirth = new Map<string, boolean>()
  // objectId → CanTouch（rbxl 由来・静的）。Blockly は CanTouch を変えないので
  // 実行時 mutable な World 状態には載せず、ここに保持する。
  // 初速（initialVelocity）を適用済みのオブジェクト。再アンカー→再解除しても
  // 初速は最初の動的化のときだけ与える（発射体の速度が何度も復活しない）。
  const initialVelocityAppliedIds = new Set<string>()
  // 生成時から非 Anchored でも動的剛体にしてよい opt-in（descriptor.dynamicAtBirth）。
  // 通常は生成時非 Anchored の部品を Fixed 据え置きにする（ウェルド集合の崩壊回避）が、
  // ここに登録された単体オブジェクト（例: RJS01 の ①1つめのボール）は、生成時固定でも
  // 1 度目の sync で動的化する。アンカー切替（魔球の停止/再開）にも追随する。
  const dynamicAtBirthIds = new Set<string>()
  // 再アンカー（うごかないようにする）時に退避した動的剛体の速度。再度アンカー
  // 解除（うごくようにする）したとき「止まる前の動き」を復元する（Roblox の
  // Anchored は速度を保持する＝彼らの版のデフォルト挙動。RJS04 の魔球など）。
  const savedDynamicVelocities = new Map<
    string,
    {
      lin: { x: number; y: number; z: number }
      ang: { x: number; y: number; z: number }
    }
  >()
  // Anchored 変化で速度を恒久的にゼロ化する rbxl Script を持つオブジェクト
  // （例: RJS01 の Ball2Script）。再アンカー時の速度退避（上記
  // savedDynamicVelocities）をスキップし、次のアンカー解除では必ず静止状態
  // から再開させる（Script の意図どおり、停止前の動きを引き継がない）。
  const permanentZeroVelocityIds = new Set<string>()
  const appliedPhysicalProperties = new Map<
    string,
    { friction: number; elasticity: number }
  >()
  // collider に適用した直近モード。変化時だけ反映し、毎フレーム呼び出しを避ける。
  // 多数のコライダー（数百〜）を毎フレーム setEnabled/setSensor すると rapier WASM が
  // 破綻するケースがあるため差分キャッシュする。
  const colliderModeCache = new Map<string, ColliderMode>()
  // 非 dynamic body へ「最後に適用した姿勢」と「最後に観測した state 参照」。
  // 状態は immutable（更新時に新オブジェクト）なので、参照比較だけで変化を
  // 毎ティック低コストで検知できる。姿勢の比較は常に body へ適用済みの pose を
  // 基準にする（観測 state 基準だと、許容誤差未満の微小 moveBy が毎ティック
  // 積み上がるケースで永久に collider が追従しなくなる）。動いたときだけ body へ
  // 適用する（同一姿勢の再適用は KCC の接触解決を乱し、斜面が登れなくなる）。
  interface AppliedPose {
    state: ObjectRuntimeState
    position: { x: number; y: number; z: number }
    rotation: { x: number; y: number; z: number; w: number }
  }
  const appliedPoses = new Map<string, AppliedPose>()
  // 動的 body の最終書き戻し位置。プログラムによる位置変更を検知し、
  // Roblox の Position 代入と同じく速度を保持してテレポートさせる。
  const lastDynamicWriteback = new Map<string, Vec3>()
  const sleepingIds = new Set<string>()
  // 動的 body のテレポート検知のしきい値（studs）。書き戻し直後〜次の同期までは
  // 物理ステップが挟まらないため、プログラム由来の移動以外で乖離は生じない。
  const DYNAMIC_TELEPORT_EPSILON = 1e-3
  const vec3Deviates = (a: Vec3, b: Vec3): boolean =>
    Math.abs(a.x - b.x) > DYNAMIC_TELEPORT_EPSILON ||
    Math.abs(a.y - b.y) > DYNAMIC_TELEPORT_EPSILON ||
    Math.abs(a.z - b.z) > DYNAMIC_TELEPORT_EPSILON
  // objectId → 現在のコライダーに適用済みのサイズ。looks_setSizeTo 等で World の
  // size が変わったらコライダーを作り直して追従させる（見た目だけ大きくなり
  // 当たり判定が古いままになるのを防ぐ）。reset 後の初期サイズ復帰も同様に検知する。
  const colliderSizes = new Map<string, { x: number; y: number; z: number }>()
  // 水域（terrain.water）。動的オブジェクトがこの中に浸かったら浮力を適用する。
  const waterVolume = descriptor.terrain?.water ?? null
  // 現在水に浸かっている動的オブジェクト。linearDamping の切替（WASM 呼び出し）を
  // 出入りの遷移時だけに抑えるための差分キャッシュ。
  const inWaterIds = new Set<string>()

  // 生成時に Anchored（固定）扱いだったか。World の judge（isStatic !== false：
  // 省略時は固定）に揃える。明示的に isStatic=false の部品（車体パーツ等）だけが
  // 非 Anchored 扱いになる。Anchored 解除で Dynamic 化してよいかの判定に使う。
  // dynamicOnUnanchor の部品（例: RJF12 の ①ボール）は非 Anchored でも World と
  // 同じく「生成時 Anchored」扱いにし、「うごくようにする」で解除されたときだけ
  // 動的化する（ブロック無しでは Fixed のまま動かない）。
  const wasAnchoredAtBirth = (object: SceneObjectDescriptor): boolean =>
    object.isStatic !== false || object.dynamicOnUnanchor === true

  /**
   * body へコライダーを 1 枚生成して取り付け、各 Map を更新する。
   * 生成（createObjectBody / createRuntimeBody）とサイズ変更時の作り直しで共用する。
   */
  // 読込済みの物理メッシュ形状（id → 正規化メッシュ）。body が存在する限り保持し、
  // dynamic 中は箱・fixed 復帰で trimesh と、適用状態だけを切り替える。
  const loadedMeshColliderSources = new Map<string, MeshColliderSource>()
  // 現在 trimesh コライダーを適用中のオブジェクト ID。
  // attachCollider はここに登録があれば箱ではなく trimesh を作る。
  const activeMeshColliderIds = new Set<string>()

  const attachCollider = (
    id: string,
    body: RAPIER.RigidBody,
    shape: ShapeKind | undefined,
    size: { x: number; y: number; z: number },
    mode: ColliderMode,
  ): void => {
    // MeshPart は読込済みなら見た目どおりの三角形メッシュで当てる
    // （箱だと恐竜の背に乗れない等、見た目と判定がずれる）。
    const meshSource =
      activeMeshColliderIds.has(id) ?
        loadedMeshColliderSources.get(id)
      : undefined
    // 当たり判定だけ見た目より小さくしたい部品（火の玉など）は collisionScale を
    // コライダーのサイズへ掛ける。見た目（size）とサイズ変更の作り直し検知
    // （colliderSizes）には元の size を使う。
    const state = world.getState().get(id)
    if (!state) throw new Error(`コライダーの状態が見つかりません: ${id}`)
    const props = physicalPropertiesFor(
      state.material,
      state.physicalProperties,
    )
    const collisionScale = state.collisionScale
    const colliderSize =
      collisionScale === 1 ? size : (
        {
          x: size.x * collisionScale,
          y: size.y * collisionScale,
          z: size.z * collisionScale,
        }
      )
    const colliderDesc =
      (meshSource ? trimeshColliderDesc(meshSource, colliderSize) : null) ??
      colliderDescForShape(shape, colliderSize)
    colliderDesc.setFriction(props.friction)
    colliderDesc.setRestitution(props.elasticity)
    colliderDesc.setFrictionCombineRule(RAPIER.CoefficientCombineRule.Average)
    colliderDesc.setRestitutionCombineRule(
      RAPIER.CoefficientCombineRule.Average,
    )
    colliderDesc.setSensor(mode === 'sensor')
    colliderDesc.setEnabled(mode !== 'off')
    // 生成時から sensor の部品も、kinematic アバターとの重なりを検知できるようにする
    // （applyColliderMode と同じ理由。既定では KINEMATIC_FIXED ペアが判定されない）。
    if (mode === 'sensor') {
      colliderDesc.setActiveCollisionTypes(SENSOR_ACTIVE_COLLISION_TYPES)
    }
    const collider = physicsWorld.createCollider(colliderDesc, body)
    appliedPhysicalProperties.set(id, {
      friction: props.friction,
      elasticity: props.elasticity,
    })
    objectColliders.set(id, collider)
    colliderToObjectId.set(collider.handle, id)
    colliderSizes.set(id, { x: size.x, y: size.y, z: size.z })
  }

  /**
   * Fixed body を生成して各 Map に登録する共通処理。
   * 非 dynamic のオブジェクトは常に Fixed body にする。プログラム（moveBy 等）
   * による移動は離散的なテレポートとして Fixed のまま setTranslation で追従する。
   * kinematic body は KCC との相性が悪く、平らな段差（autostep）や斜面の登坂が
   * body 種別によって効かなくなるため使わない（衝突挙動を全オブジェクトで共通化）。
   */
  const registerBody = (args: {
    id: string
    shape: ShapeKind | undefined
    size: { x: number; y: number; z: number }
    position: { x: number; y: number; z: number }
    rotation: { x: number; y: number; z: number; w: number }
    canCollide: boolean
    canTouch: boolean
    anchoredAtBirth: boolean
    state: ObjectRuntimeState | undefined
  }): void => {
    const bodyDesc = RAPIER.RigidBodyDesc.fixed()
    bodyDesc.setTranslation(args.position.x, args.position.y, args.position.z)
    // 描画と同じ「Y ヨー ∘ 基準向き」をコライダーにも与え、傾いた板/ランプや
    // 90° 回した床の当たり判定を描画に揃える（黄色い床のずれの原因）。
    bodyDesc.setRotation(args.rotation)
    const body = physicsWorld.createRigidBody(bodyDesc)
    const mode = colliderModeFor(args.canCollide, args.canTouch)
    attachCollider(args.id, body, args.shape, args.size, mode)
    objectBodies.set(args.id, body)
    objectWasAnchoredAtBirth.set(args.id, args.anchoredAtBirth)
    // 差分検出キャッシュへ初期モードを登録（次フレーム以降の再適用抑制に使う）。
    colliderModeCache.set(args.id, mode)
    // 移動検知（差分適用）の基準。生成時に body へ与えた姿勢そのもの。
    if (args.state) {
      appliedPoses.set(args.id, {
        state: args.state,
        position: { ...args.position },
        rotation: { ...args.rotation },
      })
    }
  }

  /**
   * 既存コライダーを除去して現在のモードで作り直す（trimesh 適用・サイズ変更用）。
   * body の姿勢・モードキャッシュは据え置く。
   */
  const replaceCollider = (
    id: string,
    body: RAPIER.RigidBody,
    shape: ShapeKind | undefined,
    size: { x: number; y: number; z: number },
  ): void => {
    const oldCollider = objectColliders.get(id)
    if (oldCollider) {
      colliderToObjectId.delete(oldCollider.handle)
      physicsWorld.removeCollider(oldCollider, false)
    }
    attachCollider(id, body, shape, size, colliderModeCache.get(id) ?? 'solid')
  }

  const createObjectBody = (object: SceneObjectDescriptor): void => {
    const initialState = world.getState().get(object.id)
    if (!initialState) return
    const baseOrientation = initialState?.orientation ?? {
      x: 0,
      y: 0,
      z: 0,
      w: 1,
    }
    registerBody({
      id: object.id,
      shape: object.shape,
      size: initialState.size,
      position: initialState.position,
      rotation: composeYaw(baseOrientation, initialState?.rotationY ?? 0),
      // CanCollide は World 状態を単一の真実源とする（rbxl 由来値 ?? 透明度推定）。
      // CanTouch は descriptor 由来（既定 true）。
      canCollide: initialState?.canCollide ?? true,
      canTouch: initialState?.canTouch ?? object.canTouch ?? true,
      anchoredAtBirth: wasAnchoredAtBirth(object),
      state: initialState,
    })
    // 単体の自由な物理オブジェクト（例: RJS01 の ①1つめのボール）は、生成時
    // 非 Anchored でも動的剛体として扱う（重力で落下・転がり・押せる）。
    if (object.dynamicAtBirth === true) {
      dynamicAtBirthIds.add(object.id)
    }
    if (hasPermanentAnchorZeroVelocityScript(descriptor.missionId, object)) {
      permanentZeroVelocityIds.add(object.id)
    }
    // MeshPart（meshUrl あり・meshScale なし＝Part.Size の箱いっぱいに引き
    // 伸ばされる描画）は、読込後に見た目どおりの trimesh コライダーへ差し替える
    // （箱だと恐竜の背の上に浮く等、乗る・ぶつかる位置が見た目とずれる）。
    // 対象は「衝突あり・一定以上の大きさ」に限定する:
    // - SpecialMesh（meshScale あり）は Part.Size と描画が一致しないため対象外
    //   （実機も衝突は Part の箱）。
    // - CanCollide=false（接触検知のみの装飾）や小物は箱のままで体感差がなく、
    //   メッシュの多いシーン（RJS02 等）での生成コスト・メモリを抑える。
    // 生成時から非 Anchored の部品（車体などのウェルド集合）も対象に含める。
    // これらは Fixed 据え置き方針で動的化しないため trimesh で問題なく、
    // 箱のままだと車の上に立つと宙に浮く（恐竜と同じ問題が RJF01/RJF10/
    // RJS09/RJS12 の車で発生）。
    // 動的化する部品は dynamic 切替時に箱へ戻し、再アンカー・reset で復元する。
    const wantsMeshCollider =
      object.meshUrl !== undefined &&
      object.meshScale === undefined &&
      canCollideAtBirth(object) &&
      Math.max(object.size.x, object.size.y, object.size.z) >=
        MESH_COLLIDER_MIN_EXTENT
    if (wantsMeshCollider && object.meshUrl) {
      void loadMeshColliderSource(object.meshUrl).then((source) => {
        if (!source || isDisposed()) {
          return
        }
        const body = objectBodies.get(object.id)
        if (!body || !world.getState().has(object.id)) {
          return
        }
        loadedMeshColliderSources.set(object.id, source)
        if (dynamicObjectIds.has(object.id)) {
          // 動的化中は箱のまま。再アンカー・reset 時に trimesh を適用する。
          return
        }
        activeMeshColliderIds.add(object.id)
        const state = world.getState().get(object.id)
        replaceCollider(
          object.id,
          body,
          state?.shape ?? object.shape,
          state?.size ?? object.size,
        )
      })
    }
  }

  /**
   * 実行時に追加されたオブジェクト（control_clone / ミッションビヘイビアの
   * addObject）の body を World 状態から生成する。descriptor に無いため
   * CanTouch は既定 true。クローン等で生成された足場にも乗れる・ぶつかる。
   */
  const createRuntimeBody = (id: string, state: ObjectRuntimeState): void => {
    registerBody({
      id,
      shape: state.shape,
      size: state.size,
      position: state.position,
      rotation: composeYaw(state.orientation, state.rotationY),
      canCollide: state.canCollide,
      canTouch: state.canTouch,
      // 実行時生成は「Anchored で生まれた」扱いにする。isStatic=false で生成
      // された clone は直後の同期で Anchored 解除遷移に入り、Roblox 同様に
      // 重力で落下する（シーン由来の born-unanchored 据え置き方針はウェルド
      // 集合の崩壊対策であり、単体 clone には適用しない）。
      anchoredAtBirth: true,
      state,
    })
  }

  /**
   * コライダーを rapier の broad phase（クエリ構造）へ再登録させる。
   *
   * 落下などで遠方へ移動した Fixed ボディを setTranslation でテレポート復帰
   * させても、broad phase の AABB が更新されず、KCC（アバターの衝突解決）や
   * レイキャストから「存在しない」扱いになることがある（実害: RJF01 で
   * アンカー解除 → 落下した壁をリセットで戻すと、見た目・body・collider の
   * 値はすべて正常なのにアバターがすり抜ける）。
   *
   * ここでは一旦 disable し、モードキャッシュを破棄して次の同期
   * （syncObjectBodies → applyColliderMode）で state に応じた有効化をやり直す。
   * 同一フレーム内で enabled をトグルすると変更が相殺されて broad phase に
   * 反映されないため、フレームを跨いだ「無効 → 有効」で確実に再追加させる。
   */
  const reregisterCollider = (id: string): void => {
    if (registryDisposed) return
    const collider = objectColliders.get(id)
    if (collider) {
      collider.setEnabled(false)
      colliderModeCache.delete(id)
    }
  }

  /** PreviewWorld の state を見て rapier 側のオブジェクト body/collider を同期する。 */
  /** オブジェクト body を rapier と全 Map/Set から除去する（destroy・reset 用）。 */
  const removeObjectBody = (id: string, body: RAPIER.RigidBody): void => {
    const collider = objectColliders.get(id)
    if (collider) {
      colliderToObjectId.delete(collider.handle)
    }
    physicsWorld.removeRigidBody(body)
    objectBodies.delete(id)
    objectColliders.delete(id)
    appliedPhysicalProperties.delete(id)
    dynamicObjectIds.delete(id)
    lastDynamicWriteback.delete(id)
    sleepingIds.delete(id)
    initialVelocityAppliedIds.delete(id)
    dynamicAtBirthIds.delete(id)
    savedDynamicVelocities.delete(id)
    permanentZeroVelocityIds.delete(id)
    objectWasAnchoredAtBirth.delete(id)
    colliderModeCache.delete(id)
    appliedPoses.delete(id)
    colliderSizes.delete(id)
    loadedMeshColliderSources.delete(id)
    activeMeshColliderIds.delete(id)
    inWaterIds.delete(id)
  }

  const applyVelocityRequest = (id: string, body: RAPIER.RigidBody): void => {
    const request = world.consumeObjectVelocityRequest(id)
    if (request?.linear) body.setLinvel(request.linear, true)
    if (request?.angular) body.setAngvel(request.angular, true)
  }

  const syncObjectBodies = (): void => {
    if (registryDisposed) return
    const states = world.getState()
    // リプレイで復元された descriptor オブジェクトや実行時追加オブジェクトの body を生成する。
    // body 数と state 数（アバター除く）が一致している通常フレームは走査しない。
    const expectedBodies = states.size - (states.has(AVATAR_ID) ? 1 : 0)
    if (objectBodies.size < expectedBodies) {
      for (const [id, state] of states) {
        if (id !== AVATAR_ID && !objectBodies.has(id)) {
          const object = descriptorObjectsById.get(id)
          if (object) {
            createObjectBody(object)
          } else {
            createRuntimeBody(id, state)
          }
        }
      }
    }
    for (const [id, body] of objectBodies) {
      const state = states.get(id)
      if (!state) {
        // destroy 済み。rapier からも除去し、全 Map/Set から削除する。
        removeObjectBody(id, body)
        continue
      }
      if (state.position.y < FALLEN_PARTS_DESTROY_HEIGHT) {
        world.destroy(id)
        removeObjectBody(id, body)
        continue
      }
      // アンカー状態に応じて fixed↔dynamic を切り替える（Anchored 解除＝重力で動く）。
      // ただし「解除」は生成時 Anchored だった部品が実行時に外された遷移に限る。
      // 生成時から非 Anchored の部品（車体など多数パーツのウェルド集合）は、
      // ウェルド未対応ゆえ各パーツが独立落下して崩壊するため、Fixed のまま
      // 据え置く（GAP-NONANCHORED の当初方針に揃える）。
      const wasAnchored = objectWasAnchoredAtBirth.get(id) ?? false
      const isDynamic = dynamicObjectIds.has(id)
      // 動的化の対象は「生成時 Anchored で解除された部品」に加え、生成時から
      // 非 Anchored でも dynamicAtBirth で opt-in した単体オブジェクトを含む。
      const mayBecomeDynamic = wasAnchored || dynamicAtBirthIds.has(id)
      if (mayBecomeDynamic && !state.anchored && !isDynamic) {
        // アンカー解除: 動的剛体にして重力で落下/転がりさせる。
        // 高重力(196.2)での薄床貫通を防ぐ CCD と、暴れを抑える軽い damping を付ける。
        // trimesh（MeshPart の見た目判定）は中身が空で質量を持てず動的剛体に
        // 不向きなため、動的化の前に箱コライダーへ戻す（読込済みソースは保持し、
        // 再アンカー・reset で trimesh を復元する）。
        if (activeMeshColliderIds.delete(id)) {
          replaceCollider(id, body, state.shape, state.size)
        }
        body.setBodyType(RAPIER.RigidBodyType.Dynamic, true)
        body.enableCcd(true)
        body.setLinearDamping(DYNAMIC_LINEAR_DAMPING)
        body.setAngularDamping(0.2)
        // damping を既定へ初期化したので水中判定キャッシュも破棄する。残すと
        // 「水面で再アンカー → 再解除」で applyWaterBuoyancy が入水遷移を検知
        // できず、水中なのに陸上 damping のまま浮いてしまう。
        inWaterIds.delete(id)
        const savedVelocity = savedDynamicVelocities.get(id)
        if (savedVelocity) {
          // 再アンカー前に退避した速度を復元（魔球の停止→再開で「止まる前の
          // 動き」が再生される）。退避は 1 回の解除につき 1 回だけ使う。
          body.setLinvel(savedVelocity.lin, true)
          body.setAngvel(savedVelocity.ang, true)
          savedDynamicVelocities.delete(id)
        } else if (
          state.initialVelocity &&
          !initialVelocityAppliedIds.has(id)
        ) {
          // 発射体（NewObjectSpec.initialVelocity 付きの addObject）は最初の
          // 動的化の瞬間にだけ初速を与える（Roblox の Part.Velocity 相当。例:
          // RJF05 の砲弾）。再アンカー→再解除では復活させない。
          body.setLinvel(state.initialVelocity, true)
          initialVelocityAppliedIds.add(id)
        }
        dynamicObjectIds.add(id)
        // テレポート検知の基準を、動的化した瞬間の body 位置で初期化する
        // （初回同期での誤検知を防ぐ）。
        const t0 = body.translation()
        lastDynamicWriteback.set(id, { x: t0.x, y: t0.y, z: t0.z })
      } else if (state.anchored && isDynamic) {
        // 再アンカー: Fixed body へ戻し、現在の World 位置・姿勢へ合わせる。
        const pos = {
          x: state.position.x,
          y: state.position.y,
          z: state.position.z,
        }
        const rot = composeYaw(state.orientation, state.rotationY)
        applyVelocityRequest(id, body)
        // 固定前に現在の速度を退避する（再度アンカー解除したとき「止まる前の
        // 動き」を復元するため）。Roblox の Anchored が速度を保持するのに揃える。
        // ただし rbxl 側に Anchored 変化で速度を恒久的にゼロ化する Script が
        // あるオブジェクトは、その意図どおり毎回静止状態から再開させるため
        // 退避しない（permanentZeroVelocityIds の説明を参照）。
        if (!permanentZeroVelocityIds.has(id)) {
          const lin = body.linvel()
          const ang = body.angvel()
          savedDynamicVelocities.set(id, {
            lin: { x: lin.x, y: lin.y, z: lin.z },
            ang: { x: ang.x, y: ang.y, z: ang.z },
          })
        }
        body.setLinvel({ x: 0, y: 0, z: 0 }, false)
        body.setAngvel({ x: 0, y: 0, z: 0 }, false)
        body.setBodyType(RAPIER.RigidBodyType.Fixed, true)
        body.setTranslation(pos, true)
        body.setRotation(rot, true)
        if (
          loadedMeshColliderSources.has(id) &&
          !activeMeshColliderIds.has(id)
        ) {
          // MeshPart は固定へ戻ったので trimesh を復元する（コライダーを
          // 作り直すため broad phase も新規登録され、reregister は不要）。
          activeMeshColliderIds.add(id)
          replaceCollider(id, body, state.shape, state.size)
        } else {
          reregisterCollider(id)
        }
        dynamicObjectIds.delete(id)
        lastDynamicWriteback.delete(id)
        sleepingIds.delete(id)
        // 現在状態の姿勢を適用済みなので、移動検知の基準も合わせて更新する
        // （落下で動いた姿勢との差分を「プログラムによる移動」と誤検知しない）。
        appliedPoses.set(id, { state, position: pos, rotation: rot })
      }
      // (un)setCanCollide で衝突可否が変わったら solid/sensor/off を切り替える。
      // CanTouch=true の非衝突部品は sensor として接触検知に残す（Touched を死なせない）。
      // モード適用は変化時のみ（毎フレーム呼ぶと多数コライダー時に WASM 破綻あり）。
      const collider = objectColliders.get(id)
      if (collider) {
        const props = physicalPropertiesFor(
          state.material,
          state.physicalProperties,
        )
        const applied = appliedPhysicalProperties.get(id)
        if (
          applied?.friction !== props.friction ||
          applied?.elasticity !== props.elasticity
        ) {
          collider.setFriction(props.friction)
          collider.setRestitution(props.elasticity)
          appliedPhysicalProperties.set(id, {
            friction: props.friction,
            elasticity: props.elasticity,
          })
        }
        // CanTouch も state を真実源にする（環境ビヘイビアの setCanTouch で
        // 実行時に不活性化できる。例: ServerStorage 相当のテンプレート）。
        const mode = colliderModeFor(state.canCollide, state.canTouch)
        if (colliderModeCache.get(id) !== mode) {
          const wasDisabled = !collider.isEnabled()
          applyColliderMode(collider, mode)
          colliderModeCache.set(id, mode)
          // 無効コライダーの再有効化では rapier が親 body の質量を再計算しない
          // （質量は Dynamic 化・コライダー取り付け・明示的な再計算でのみ更新
          // される）。'off' のまま動的化した body は質量 0 で重力が効かず宙に
          // 浮き続けるため（例: RJS10 の隠蔽テンプレート由来クローンが Y=100 に
          // 溜まる）、無効だったコライダーが有効なモードへ復帰した時点で質量を
          // 計算し直して落下を始めさせる（既に有効な solid↔sensor 間の切替は
          // 質量が変わらないので再計算しない）。
          // 既知の制限: 'off' へ変えた動的 body の質量は rapier 任せで一貫しない
          // （飛行中は質量を保持して落下継続、静止後は 0 になり宙に固まる）。
          // Roblox の「CanCollide=false でも落下する」へ完全に揃えるには 'off' を
          // 慣性 sensor 化する等のコライダー設計変更が必要で、別変更とする。
          if (mode !== 'off' && dynamicObjectIds.has(id)) {
            if (wasDisabled) {
              body.recomputeMassPropertiesFromColliders()
            }
            body.wakeUp()
          }
        }
      }
      // 位置・向き同期（非 dynamic、Fixed body の離散テレポート）。
      // プログラム（moveBy/turnBy 等）で動かされた部品の当たり判定を見た目に
      // 追従させる。状態は immutable のため参照が同じなら何もせず（動かない
      // 大多数の床は参照比較 1 回で済む）、参照が変わったときだけ「適用済み
      // 姿勢」と望ましい姿勢を値で比較する（色変更などの無関係な更新や、
      // 同値での再代入では何もしない）。基準が無い場合（生成・reset 直後の
      // 初回観測）は body の現姿勢を基準にし、最初の同期前にプログラムが
      // 動かしたケースも取りこぼさない。
      // 実際に動いたときだけ適用するのは、同一姿勢の再適用でも KCC の接触解決を
      // 乱して斜面の登坂が止まる事例があるため（斜面歩行は共通動作として保つ）。
      if (!dynamicObjectIds.has(id)) {
        world.consumeObjectVelocityRequest(id)
        const cached = appliedPoses.get(id)
        if (cached?.state !== state) {
          syncColliderSize(id, body, state)
          const desiredPosition = {
            x: state.position.x,
            y: state.position.y,
            z: state.position.z,
          }
          const desiredRotation = composeYaw(state.orientation, state.rotationY)
          const basePosition = cached ? cached.position : body.translation()
          const baseRotation = cached ? cached.rotation : body.rotation()
          const moved =
            !vec3Equals(basePosition, desiredPosition) ||
            !quatEquals(baseRotation, desiredRotation)
          if (moved) {
            body.setTranslation(desiredPosition, true)
            body.setRotation(desiredRotation, true)
            // 移動でアバターを巻き込んだら押し出す（Roblox の Anchored
            // パーツが Humanoid を押す挙動の近似）。
            onAnchoredProgramMove(
              id,
              state,
              basePosition,
              desiredPosition,
              objectColliders.get(id)?.handle,
            )
            appliedPoses.set(id, {
              state,
              position: desiredPosition,
              rotation: desiredRotation,
            })
          } else {
            // 姿勢は据え置き、観測済み state の参照だけ進める（微小差は基準を
            // 進めない＝積み上がればいずれ閾値を超えて適用される）。
            appliedPoses.set(id, {
              state,
              position: basePosition,
              rotation: baseRotation,
            })
          }
        }
      } else {
        // 物理の書き戻し位置からの乖離はプログラムによるテレポート。
        // Roblox の Position 代入と同じく、線速度・角速度を保持する。
        const lastPosition = lastDynamicWriteback.get(id)
        if (lastPosition && vec3Deviates(state.position, lastPosition)) {
          const teleport = { ...state.position }
          body.setTranslation(teleport, true)
          lastDynamicWriteback.set(id, teleport)
        }
        // 明示的な速度要求は、動的化時の退避速度・初速の復元後に適用する。
        // テレポートと同じステップでも要求を消費し、眠っている body も起こす。
        applyVelocityRequest(id, body)
        // サイズだけは毎ティック比較して追従する（比較は数値3つで軽量）。
        syncColliderSize(id, body, state)
        applyWaterBuoyancy(id, body, state)
      }
    }
  }

  /**
   * 水域（terrain.water）に浸かった動的オブジェクトへ浮力を適用する。
   * 教材仕様「ボールは水に浮かせる」の実現で、材質・密度によらず一律に、
   * 平衡点（水面から部品の高さ × 0.2 沈んだ中心位置）より深ければ一定の
   * 浮上速度を与え、水中は抗力を強めて入水の勢い・水面の揺れを減衰させる
   * （アバターの簡易遊泳と同型の、速度ベースの近似。例: RJF09 のボール）。
   * 実機 Roblox の Terrain 水の浮力は材質密度に依存する（RJF09 のボールは
   * Glass 密度 2.4 で実機では沈む）が、密度は再現せず「浮く」に揃える。
   * damping の切替（WASM 呼び出し）は水への出入りの遷移時だけ行う。
   */
  const applyWaterBuoyancy = (
    id: string,
    body: RAPIER.RigidBody,
    state: ObjectRuntimeState,
  ): void => {
    if (!waterVolume) {
      return
    }
    const translation = body.translation()
    const halfHeight = state.size.y / 2
    // 下端が水面下に入っていれば「水に触れている」（矩形は水平方向の範囲）。
    const touchesWater =
      translation.x >= waterVolume.x0 &&
      translation.x <= waterVolume.x1 &&
      translation.z >= waterVolume.z0 &&
      translation.z <= waterVolume.z1 &&
      translation.y - halfHeight < waterVolume.level
    if (!touchesWater) {
      if (inWaterIds.delete(id)) {
        body.setLinearDamping(DYNAMIC_LINEAR_DAMPING)
      }
      return
    }
    if (!inWaterIds.has(id)) {
      inWaterIds.add(id)
      body.setLinearDamping(OBJECT_WATER_LINEAR_DAMPING)
    }
    const floatCenterY =
      waterVolume.level - state.size.y * OBJECT_FLOAT_CENTER_DEPTH_PER_HEIGHT
    if (translation.y < floatCenterY) {
      const velocity = body.linvel()
      if (velocity.y < OBJECT_FLOAT_BUOYANCY_SPEED) {
        body.setLinvel(
          { x: velocity.x, y: OBJECT_FLOAT_BUOYANCY_SPEED, z: velocity.z },
          true,
        )
      }
    }
  }

  /**
   * looks_setSizeTo 等でサイズが変わったらコライダーを作り直して追従する
   * （body の姿勢はそのまま）。形状は実行時に変わらないので shape は据え置き。
   */
  const syncColliderSize = (
    id: string,
    body: RAPIER.RigidBody,
    state: ObjectRuntimeState,
  ): void => {
    const knownSize = colliderSizes.get(id)
    if (!knownSize || vec3Equals(knownSize, state.size)) {
      return
    }
    const expanded =
      state.size.x > knownSize.x ||
      state.size.y > knownSize.y ||
      state.size.z > knownSize.z
    replaceCollider(id, body, state.shape, state.size)
    if (expanded && state.canCollide) {
      onProgramResize(
        id,
        state,
        knownSize,
        state.size,
        objectColliders.get(id)?.handle,
      )
    }
  }

  /**
   * 動的剛体（アンカー解除済み）の確定姿勢を World へ書き戻し、描画へ反映する。
   * 球の転がり等のタンブリングを表すため位置と完全姿勢を渡す。静止（sleeping）した
   * body は最終状態を一度だけ書き戻し、まとめて 1 回の通知で再描画コストを抑える。
   */
  const writeBackDynamicBodies = (): void => {
    if (registryDisposed) return
    const dynamicUpdates: {
      objectId: string
      position: { x: number; y: number; z: number }
      orientation: { x: number; y: number; z: number; w: number }
      velocity: Vec3
      angularVelocity: Vec3
    }[] = []
    for (const id of dynamicObjectIds) {
      const body = objectBodies.get(id)
      if (!body) continue
      if (body.isSleeping()) {
        if (sleepingIds.has(id)) continue
        sleepingIds.add(id)
      } else {
        sleepingIds.delete(id)
      }
      const t = body.translation()
      const r = body.rotation()
      const v = body.linvel()
      const angularVelocity = body.angvel()
      dynamicUpdates.push({
        objectId: id,
        position: { x: t.x, y: t.y, z: t.z },
        orientation: { x: r.x, y: r.y, z: r.z, w: r.w },
        velocity: { x: v.x, y: v.y, z: v.z },
        angularVelocity: {
          x: angularVelocity.x,
          y: angularVelocity.y,
          z: angularVelocity.z,
        },
      })
      // 物理が書き戻した位置を記録し、次の同期でプログラムによるテレポートを検知する。
      lastDynamicWriteback.set(id, { x: t.x, y: t.y, z: t.z })
    }
    world.setDynamicTransforms(dynamicUpdates)
  }

  // 全オブジェクト body を descriptor の初期状態へ戻す。アンカー解除で動的化して
  // 転がった部品も、body 種別・姿勢・速度を初期化して確実に元位置へ戻す
  // （非 Anchored 部品など anchored が初期から false の場合も取りこぼさない）。
  const resetObjectBodies = (): void => {
    if (registryDisposed) return
    dynamicObjectIds.clear()
    lastDynamicWriteback.clear()
    sleepingIds.clear()
    // 初速の適用記録もクリアする（リセット後の新しいプレイでは、シーン由来の
    // 発射体が再び初速を得られるようにする。実行時生成の発射体は World 側の
    // reset で消えるため影響しない）。
    initialVelocityAppliedIds.clear()
    // 退避した速度もクリア（リプレイは静止状態から始める）。
    savedDynamicVelocities.clear()
    // 水中判定のキャッシュもクリア（次の動的化時に damping は既定へ戻る）。
    inWaterIds.clear()
    // 移動検知（差分適用）の基準もクリアする（World 側 reset で初期状態の参照に
    // 変わるため、次の同期で body 姿勢を基準に取り直す。残すと初期姿勢への
    // 復帰を「移動」と誤検知する）。
    appliedPoses.clear()
    // 実行時生成（clone / ビヘイビアの addObject）の body は物理からも即時除去
    // する。World 側 reset で状態は消えるが、次の物理ステップの同期前に同じ id
    // で再追加されると、前回プレイの倒れた姿勢の body が再利用されてしまう
    // （例: RJF02 のピンをリプレイで作り直すケース）。
    const descriptorIds = new Set(descriptor.objects.map((object) => object.id))
    for (const [id, body] of objectBodies) {
      if (!descriptorIds.has(id)) {
        removeObjectBody(id, body)
      }
    }
    for (const object of descriptor.objects) {
      const body = objectBodies.get(object.id)
      if (!body) {
        continue
      }
      if (!world.getState().has(object.id)) {
        removeObjectBody(object.id, body)
        continue
      }
      // 動的化して付いた速度・力を消してから Fixed へ戻す（再アンカー時の速度復活防止）。
      body.setLinvel(ZERO_VEC3, false)
      body.setAngvel(ZERO_VEC3, false)
      body.resetForces(false)
      body.resetTorques(false)
      body.setBodyType(RAPIER.RigidBodyType.Fixed, true)
      const base = object.orientation ?? { x: 0, y: 0, z: 0, w: 1 }
      const rot = composeYaw(
        base,
        object.orientation ? 0 : (object.rotationY ?? 0),
      )
      // 実際に位置が変わった（落下等で動いて戻した）コライダーだけ broad phase
      // へ再登録する。動いていないコライダーまで disable すると、リセット直後
      // （次の同期前）の足元クエリ（rememberGroundPlatform 等）が外れる。
      const before = body.translation()
      const moved =
        Math.abs(before.x - object.position.x) > 1e-3 ||
        Math.abs(before.y - object.position.y) > 1e-3 ||
        Math.abs(before.z - object.position.z) > 1e-3
      body.setTranslation(object.position, true)
      body.setRotation(rot, true)
      if (
        loadedMeshColliderSources.has(object.id) &&
        !activeMeshColliderIds.has(object.id)
      ) {
        // 動的化で箱に戻していた MeshPart は、固定へ戻したので trimesh を
        // 復元する（コライダー新規作成のため broad phase の再登録も兼ねる）。
        activeMeshColliderIds.add(object.id)
        replaceCollider(object.id, body, object.shape, object.size)
      } else if (moved) {
        reregisterCollider(object.id)
      }
    }
  }

  // dispose 後に sync 等が呼ばれても rapier へ触らないためのガード。
  let registryDisposed = false

  /**
   * レジストリを操作不能にして内部の Map/Set をすべて空にする（コントローラの
   * dispose 用）。rapier の body/collider 自体はここでは解放せず、直後の
   * physicsWorld.free() の一括解放に任せる（個別 free は WASM 借用エラーの
   * 原因になる）。dispose 後の sync/reset/writeBack/reregister は no-op。
   */
  const dispose = (): void => {
    registryDisposed = true
    objectBodies.clear()
    objectColliders.clear()
    appliedPhysicalProperties.clear()
    colliderToObjectId.clear()
    dynamicObjectIds.clear()
    lastDynamicWriteback.clear()
    sleepingIds.clear()
    appliedPoses.clear()
    colliderSizes.clear()
    colliderModeCache.clear()
    objectWasAnchoredAtBirth.clear()
    initialVelocityAppliedIds.clear()
    dynamicAtBirthIds.clear()
    permanentZeroVelocityIds.clear()
    savedDynamicVelocities.clear()
    loadedMeshColliderSources.clear()
    activeMeshColliderIds.clear()
    inWaterIds.clear()
  }

  for (const object of descriptor.objects) {
    createObjectBody(object)
  }

  return {
    objectIdOf: (handle) => colliderToObjectId.get(handle),
    modeOf: (id) => colliderModeCache.get(id),
    has: (id) => objectBodies.has(id),
    reregister: reregisterCollider,
    sync: syncObjectBodies,
    writeBackDynamicBodies,
    reset: resetObjectBodies,
    dispose,
  }
}
